interface WorkExperience {
  start?: string | null;
  end?: string | null;
  position?: string | null;
  company?: string | null;
}

interface Education {
  start?: string | null;
  end?: string | null;
  degree?: string | null;
  school?: string | null;
}

interface AgeEstimateResult {
  age: number;
  detail: string;
}

/**
 * Parse year from various date formats:
 * - "YYYY-MM" (e.g., "2020-01")
 * - "YYYY" (e.g., "2020")
 * - "M/D/YYYY" (e.g., "1/1/2020" or "12/15/2020") - Unipile format
 */
function parseYearFromDateString(dateStr: string): number | null {
  if (!dateStr) return null;

  // Try M/D/YYYY format first (Unipile format)
  const slashMatch = /^\d{1,2}\/\d{1,2}\/(\d{4})$/.exec(dateStr);
  if (slashMatch?.[1]) {
    const year = parseInt(slashMatch[1], 10);
    return isNaN(year) ? null : year;
  }

  // Try YYYY-MM or YYYY format
  const year = parseInt(dateStr.substring(0, 4), 10);
  if (!isNaN(year) && year >= 1900 && year <= 2100) {
    return year;
  }

  return null;
}

/**
 * Calculate the minimum age of a person based on their work experience history.
 * Assumes the person was at least 16 when they started their first job.
 *
 * @param workExperience - Array of work experience entries with start dates
 * @returns Object with estimated minimum age and detail string, or null if no valid dates found
 */
export function calculateMinimumAgeFromWorkExperience(
  workExperience: WorkExperience[] | null | undefined,
): AgeEstimateResult | null {
  if (!workExperience?.length) return null;

  const currentYear = new Date().getFullYear();
  let earliestStartYear: number | null = null;
  let earliestJob: WorkExperience | null = null;

  for (const exp of workExperience) {
    if (!exp.start) continue;
    const year = parseYearFromDateString(exp.start);
    if (year != null && (earliestStartYear == null || year < earliestStartYear)) {
      earliestStartYear = year;
      earliestJob = exp;
    }
  }

  if (earliestStartYear == null) return null;

  // Assume person was at least 16 when they started working
  const age = currentYear - earliestStartYear + 16;
  const detail = `earliest job: ${earliestStartYear}${earliestJob?.position ? ` (${earliestJob.position})` : ''}`;

  return { age, detail };
}

/**
 * Calculate the minimum age of a person based on their education history.
 * Prefers graduation year (end) + 21 — the typical undergraduate graduation age.
 * Falls back to start + 18 for rows with no end date (in-progress students).
 * Returns the highest candidate age across rows — the tightest floor we can defend.
 */
export function calculateMinimumAgeFromEducation(
  education: Education[] | null | undefined,
): AgeEstimateResult | null {
  if (!education?.length) return null;

  const currentYear = new Date().getFullYear();
  let best: AgeEstimateResult | null = null;

  for (const edu of education) {
    const endYear = edu.end ? parseYearFromDateString(edu.end) : null;
    if (endYear != null) {
      const age = currentYear - endYear + 21;
      const detail = `graduation: ${endYear}${edu.school ? ` (${edu.school})` : ''}`;
      if (best == null || age > best.age) best = { age, detail };
      continue;
    }
    const startYear = edu.start ? parseYearFromDateString(edu.start) : null;
    if (startYear != null) {
      const age = currentYear - startYear + 18;
      const detail = `education start: ${startYear}${edu.school ? ` (${edu.school})` : ''}`;
      if (best == null || age > best.age) best = { age, detail };
    }
  }

  return best;
}

/**
 * Calculate the best minimum age estimate from work experience and education.
 * Returns the higher estimate (more conservative) between the two sources.
 * Also exposes both sub-estimates so callers can log/inspect why one won.
 */
export function calculateMinimumAge(
  workExperience: WorkExperience[] | null | undefined,
  education: Education[] | null | undefined,
): (AgeEstimateResult & { workAge: AgeEstimateResult | null; eduAge: AgeEstimateResult | null }) | null {
  const workAge = calculateMinimumAgeFromWorkExperience(workExperience);
  const eduAge = calculateMinimumAgeFromEducation(education);

  if (workAge == null && eduAge == null) return null;

  let chosen: AgeEstimateResult;
  if (workAge == null) chosen = eduAge!;
  else if (eduAge == null) chosen = workAge;
  else chosen = workAge.age >= eduAge.age ? workAge : eduAge;

  return { ...chosen, workAge, eduAge };
}
